/**
 * ═══════════════════════════════════════════════════════════════════════════
 * NOVA'S GEMS - ADVANCED FILTERING SYSTEM
 * Visual filters, range sliders, color swatches, comparison mode
 * ═══════════════════════════════════════════════════════════════════════════
 */

class NovaAdvancedFilters {
  constructor() {
    this.filters = {
      priceRange: { min: 0, max: 100000 },
      caratRange: { min: 0, max: 20 },
      colors: [],
      origins: [],
      clarities: [],
      cuts: [],
      availability: 'all'
    };
    
    this.activeFilters = new Set();
    this.products = [];
    this.filteredProducts = [];
    this.comparisonMode = false;
    this.selectedForComparison = new Set();
    
    this.init();
  }

  init() {
    this.cacheDOM();
    this.attachEventListeners();
    this.initRangeSliders();
    this.loadProducts();
  }

  cacheDOM() {
    this.filterPanel = document.getElementById('novaAdvancedFilters');
    this.priceSlider = document.getElementById('novaPriceSlider');
    this.caratSlider = document.getElementById('novaCaratSlider');
    this.colorSwatches = document.getElementById('novaColorSwatches');
    this.originCheckboxes = document.getElementById('novaOriginFilters');
    this.clarityCheckboxes = document.getElementById('novaClarityFilters');
    this.cutCheckboxes = document.getElementById('novaCutFilters');
    this.comparisonToggle = document.getElementById('novaComparisonToggle');
    this.comparisonBar = document.getElementById('novaComparisonBar');
    this.compareButton = document.getElementById('novaCompareButton');
    this.clearFiltersBtn = document.getElementById('novaClearFilters');
    this.saveFiltersBtn = document.getElementById('novaSaveFilters');
    this.gridContainer = document.getElementById('novaGemstoneGrid');
    this.resultsCount = document.getElementById('novaResultsCount');
  }

  attachEventListeners() {
    // Price range slider
    if (this.priceSlider) {
      this.priceSlider.addEventListener('input', (e) => {
        this.filters.priceRange.max = parseInt(e.target.value);
        this.updatePriceDisplay();
        this.applyFilters();
      });
    }

    // Carat range slider
    if (this.caratSlider) {
      this.caratSlider.addEventListener('input', (e) => {
        this.filters.caratRange.max = parseFloat(e.target.value);
        this.updateCaratDisplay();
        this.applyFilters();
      });
    }

    // Color swatches
    if (this.colorSwatches) {
      this.colorSwatches.addEventListener('click', (e) => {
        const swatch = e.target.closest('.nova-color-swatch');
        if (swatch) {
          const color = swatch.dataset.color;
          this.toggleColorFilter(color, swatch);
        }
      });
    }

    // Checkboxes (origin, clarity, cut)
    [this.originCheckboxes, this.clarityCheckboxes, this.cutCheckboxes].forEach(container => {
      if (container) {
        container.addEventListener('change', (e) => {
          if (e.target.type === 'checkbox') {
            this.handleCheckboxChange(e.target);
          }
        });
      }
    });

    // Comparison mode
    if (this.comparisonToggle) {
      this.comparisonToggle.addEventListener('click', () => {
        this.toggleComparisonMode();
      });
    }

    if (this.compareButton) {
      this.compareButton.addEventListener('click', () => {
        this.showComparisonModal();
      });
    }

    // Clear filters
    if (this.clearFiltersBtn) {
      this.clearFiltersBtn.addEventListener('click', () => {
        this.clearAllFilters();
      });
    }

    // Save filters
    if (this.saveFiltersBtn) {
      this.saveFiltersBtn.addEventListener('click', () => {
        this.saveFilterPreset();
      });
    }

    // Grid card clicks (for comparison)
    if (this.gridContainer) {
      this.gridContainer.addEventListener('click', (e) => {
        const compareCheckbox = e.target.closest('.nova-compare-checkbox');
        if (compareCheckbox && this.comparisonMode) {
          const card = compareCheckbox.closest('.nova-gem-card');
          this.toggleProductComparison(card);
        }
      });
    }
  }

  initRangeSliders() {
    // noUiSlider or custom implementation
    // For now, using native range inputs with enhanced styling
    this.updatePriceDisplay();
    this.updateCaratDisplay();
  }

  updatePriceDisplay() {
    const display = document.getElementById('novaPriceDisplay');
    if (display) {
      const max = this.filters.priceRange.max;
      display.textContent = `Up to $${max.toLocaleString()}`;
    }
  }

  updateCaratDisplay() {
    const display = document.getElementById('novaCaratDisplay');
    if (display) {
      const max = this.filters.caratRange.max;
      display.textContent = `Up to ${max} ct`;
    }
  }

  toggleColorFilter(color, swatch) {
    const index = this.filters.colors.indexOf(color);
    if (index === -1) {
      this.filters.colors.push(color);
      swatch.classList.add('active');
    } else {
      this.filters.colors.splice(index, 1);
      swatch.classList.remove('active');
    }
    this.applyFilters();
  }

  handleCheckboxChange(checkbox) {
    const filterType = checkbox.dataset.filterType;
    const value = checkbox.value;

    if (checkbox.checked) {
      this.filters[filterType].push(value);
    } else {
      const index = this.filters[filterType].indexOf(value);
      if (index > -1) {
        this.filters[filterType].splice(index, 1);
      }
    }

    this.applyFilters();
  }

  async loadProducts() {
    try {
      // Get products from page or fetch
      const cards = document.querySelectorAll('.nova-gem-card');
      this.products = Array.from(cards).map(card => ({
        element: card,
        handle: card.dataset.handle,
        price: parseFloat(card.dataset.price),
        carat: parseFloat(card.dataset.carat) || 0,
        color: card.dataset.color || '',
        origin: card.dataset.origin || '',
        clarity: card.dataset.clarity || '',
        cut: card.dataset.cut || '',
        available: card.dataset.available === 'true'
      }));

      this.filteredProducts = [...this.products];
    } catch (error) {
      console.error('Failed to load products:', error);
    }
  }

  applyFilters() {
    this.filteredProducts = this.products.filter(product => {
      // Price filter
      if (product.price > this.filters.priceRange.max) {
        return false;
      }

      // Carat filter
      if (product.carat > this.filters.caratRange.max) {
        return false;
      }

      // Color filter
      if (this.filters.colors.length > 0 && !this.filters.colors.includes(product.color)) {
        return false;
      }

      // Origin filter
      if (this.filters.origins.length > 0 && !this.filters.origins.includes(product.origin)) {
        return false;
      }

      // Clarity filter
      if (this.filters.clarities.length > 0 && !this.filters.clarities.includes(product.clarity)) {
        return false;
      }

      // Cut filter
      if (this.filters.cuts.length > 0 && !this.filters.cuts.includes(product.cut)) {
        return false;
      }

      // Availability filter
      if (this.filters.availability === 'in_stock' && !product.available) {
        return false;
      }

      return true;
    });

    this.updateGrid();
    this.updateResultsCount();
    this.updateActiveFiltersDisplay();
  }

  updateGrid() {
    this.products.forEach(product => {
      const isVisible = this.filteredProducts.includes(product);
      product.element.style.display = isVisible ? '' : 'none';
    });
  }

  updateResultsCount() {
    if (this.resultsCount) {
      const count = this.filteredProducts.length;
      this.resultsCount.textContent = `${count} gemstone${count !== 1 ? 's' : ''}`;
    }
  }

  updateActiveFiltersDisplay() {
    const container = document.getElementById('novaActiveFilters');
    if (!container) return;

    const activeFilters = [];

    // Price
    if (this.filters.priceRange.max < 100000) {
      activeFilters.push({
        label: `Price: Up to $${this.filters.priceRange.max.toLocaleString()}`,
        clear: () => {
          this.filters.priceRange.max = 100000;
          if (this.priceSlider) this.priceSlider.value = 100000;
          this.updatePriceDisplay();
          this.applyFilters();
        }
      });
    }

    // Carat
    if (this.filters.caratRange.max < 20) {
      activeFilters.push({
        label: `Carat: Up to ${this.filters.caratRange.max}`,
        clear: () => {
          this.filters.caratRange.max = 20;
          if (this.caratSlider) this.caratSlider.value = 20;
          this.updateCaratDisplay();
          this.applyFilters();
        }
      });
    }

    // Colors
    this.filters.colors.forEach(color => {
      activeFilters.push({
        label: `Color: ${color}`,
        clear: () => {
          this.filters.colors = this.filters.colors.filter(c => c !== color);
          const swatch = this.colorSwatches?.querySelector(`[data-color="${color}"]`);
          if (swatch) swatch.classList.remove('active');
          this.applyFilters();
        }
      });
    });

    // Origins
    this.filters.origins.forEach(origin => {
      activeFilters.push({
        label: `Origin: ${origin}`,
        clear: () => {
          this.filters.origins = this.filters.origins.filter(o => o !== origin);
          const checkbox = this.originCheckboxes?.querySelector(`[value="${origin}"]`);
          if (checkbox) checkbox.checked = false;
          this.applyFilters();
        }
      });
    });

    // Render active filters
    container.innerHTML = activeFilters.map(filter => `
      <button class="nova-active-filter" onclick="this.dispatchEvent(new CustomEvent('clear', {bubbles: true}))">
        ${filter.label}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    `).join('');

    // Attach clear handlers
    const filterBtns = container.querySelectorAll('.nova-active-filter');
    filterBtns.forEach((btn, index) => {
      btn.addEventListener('clear', activeFilters[index].clear);
    });
  }

  clearAllFilters() {
    this.filters = {
      priceRange: { min: 0, max: 100000 },
      caratRange: { min: 0, max: 20 },
      colors: [],
      origins: [],
      clarities: [],
      cuts: [],
      availability: 'all'
    };

    // Reset UI
    if (this.priceSlider) this.priceSlider.value = 100000;
    if (this.caratSlider) this.caratSlider.value = 20;
    
    const allSwatches = this.colorSwatches?.querySelectorAll('.nova-color-swatch');
    allSwatches?.forEach(s => s.classList.remove('active'));

    const allCheckboxes = document.querySelectorAll('.nova-filter-checkbox');
    allCheckboxes.forEach(cb => cb.checked = false);

    this.updatePriceDisplay();
    this.updateCaratDisplay();
    this.applyFilters();
  }

  saveFilterPreset() {
    const presetName = prompt('Name this filter preset:');
    if (!presetName) return;

    const preset = {
      name: presetName,
      filters: JSON.parse(JSON.stringify(this.filters)),
      timestamp: Date.now()
    };

    // Save to localStorage
    const presets = JSON.parse(localStorage.getItem('novaFilterPresets') || '[]');
    presets.push(preset);
    localStorage.setItem('novaFilterPresets', JSON.stringify(presets));

    this.showToast(`Filter preset "${presetName}" saved!`);
  }

  loadFilterPreset(preset) {
    this.filters = JSON.parse(JSON.stringify(preset.filters));
    
    // Update UI to match loaded preset
    if (this.priceSlider) this.priceSlider.value = this.filters.priceRange.max;
    if (this.caratSlider) this.caratSlider.value = this.filters.caratRange.max;
    
    this.updatePriceDisplay();
    this.updateCaratDisplay();
    this.applyFilters();
  }

  toggleComparisonMode() {
    this.comparisonMode = !this.comparisonMode;
    
    if (this.comparisonMode) {
      this.comparisonToggle?.classList.add('active');
      this.comparisonBar?.classList.add('visible');
      document.body.classList.add('nova-comparison-active');
      
      // Show checkboxes on cards
      document.querySelectorAll('.nova-gem-card').forEach(card => {
        card.classList.add('comparison-mode');
      });
    } else {
      this.comparisonToggle?.classList.remove('active');
      this.comparisonBar?.classList.remove('visible');
      document.body.classList.remove('nova-comparison-active');
      
      // Hide checkboxes
      document.querySelectorAll('.nova-gem-card').forEach(card => {
        card.classList.remove('comparison-mode');
      });
      
      this.selectedForComparison.clear();
      this.updateComparisonBar();
    }
  }

  toggleProductComparison(card) {
    const handle = card.dataset.handle;
    
    if (this.selectedForComparison.has(handle)) {
      this.selectedForComparison.delete(handle);
      card.classList.remove('selected-for-comparison');
    } else {
      if (this.selectedForComparison.size >= 4) {
        this.showToast('Maximum 4 products can be compared');
        return;
      }
      this.selectedForComparison.add(handle);
      card.classList.add('selected-for-comparison');
    }

    this.updateComparisonBar();
  }

  updateComparisonBar() {
    const count = this.selectedForComparison.size;
    const countDisplay = document.getElementById('novaComparisonCount');
    
    if (countDisplay) {
      countDisplay.textContent = count;
    }

    if (this.compareButton) {
      this.compareButton.disabled = count < 2;
    }
  }

  async showComparisonModal() {
    if (this.selectedForComparison.size < 2) return;

    const modal = document.createElement('div');
    modal.className = 'nova-comparison-modal';
    modal.innerHTML = `
      <div class="nova-comparison-overlay"></div>
      <div class="nova-comparison-content">
        <div class="nova-comparison-header">
          <h2>Compare Gemstones</h2>
          <button class="nova-modal-close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div class="nova-comparison-grid" id="novaComparisonGrid">
          <div class="nova-comparison-loading">Loading comparison data...</div>
        </div>
      </div>
    `;

    document.body.appendChild(modal);
    document.body.style.overflow = 'hidden';

    // Close button
    modal.querySelector('.nova-modal-close').addEventListener('click', () => {
      document.body.removeChild(modal);
      document.body.style.overflow = '';
    });

    // Overlay click
    modal.querySelector('.nova-comparison-overlay').addEventListener('click', () => {
      document.body.removeChild(modal);
      document.body.style.overflow = '';
    });

    // Load comparison data
    await this.loadComparisonData(modal);
  }

  async loadComparisonData(modal) {
    const grid = modal.querySelector('#novaComparisonGrid');
    const handles = Array.from(this.selectedForComparison);

    try {
      // Fetch product data
      const products = await Promise.all(
        handles.map(handle => fetch(`/products/${handle}.js`).then(r => r.json()))
      );

      // Build comparison table
      grid.innerHTML = `
        <table class="nova-comparison-table">
          <thead>
            <tr>
              <th>Feature</th>
              ${products.map(p => `<th><img src="${p.featured_image}" alt="${p.title}"><br>${p.title}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Price</td>
              ${products.map(p => `<td><strong>$${(p.price / 100).toFixed(0)}</strong></td>`).join('')}
            </tr>
            <tr>
              <td>Carat Weight</td>
              ${products.map(p => `<td>${this.extractCarat(p.title) || '-'}</td>`).join('')}
            </tr>
            <tr>
              <td>Color</td>
              ${products.map(p => `<td>${this.extractColor(p.tags) || '-'}</td>`).join('')}
            </tr>
            <tr>
              <td>Clarity</td>
              ${products.map(p => `<td>${this.extractClarity(p.tags) || '-'}</td>`).join('')}
            </tr>
            <tr>
              <td>Origin</td>
              ${products.map(p => `<td>${this.extractOrigin(p.tags) || '-'}</td>`).join('')}
            </tr>
            <tr>
              <td>3D Model</td>
              ${products.map(p => `<td>${this.has3DModel(p) ? '✅ Available' : '❌ Not available'}</td>`).join('')}
            </tr>
            <tr>
              <td>Availability</td>
              ${products.map(p => `<td>${p.available ? '✅ In Stock' : '❌ Sold Out'}</td>`).join('')}
            </tr>
            <tr>
              <td>Action</td>
              ${products.map(p => `<td><a href="/products/${p.handle}" class="nova-comparison-cta">View Details</a></td>`).join('')}
            </tr>
          </tbody>
        </table>
      `;
    } catch (error) {
      console.error('Failed to load comparison data:', error);
      grid.innerHTML = '<p class="nova-error">Failed to load comparison data</p>';
    }
  }

  extractCarat(title) {
    const match = title.match(/(\d+\.?\d*)\s*ct/i);
    return match ? match[1] + ' ct' : null;
  }

  extractColor(tags) {
    const colors = ['Blue', 'Red', 'Green', 'Yellow', 'Pink', 'Purple', 'White'];
    return tags.find(tag => colors.some(c => tag.toLowerCase().includes(c.toLowerCase())));
  }

  extractClarity(tags) {
    const clarities = ['VVS', 'VS', 'SI', 'IF', 'FL', 'I1', 'I2'];
    return tags.find(tag => clarities.some(c => tag.includes(c)));
  }

  extractOrigin(tags) {
    const origins = ['Sri Lanka', 'Ceylon', 'Madagascar', 'Myanmar', 'Kashmir', 'Colombia'];
    return tags.find(tag => origins.some(o => tag.includes(o)));
  }

  has3DModel(product) {
    return product.media && product.media.some(m => m.media_type === 'model');
  }

  showToast(message) {
    const toast = document.createElement('div');
    toast.className = 'nova-toast';
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => toast.classList.add('visible'), 100);
    setTimeout(() => {
      toast.classList.remove('visible');
      setTimeout(() => document.body.removeChild(toast), 300);
    }, 3000);
  }
}

// Initialize when DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.NovaFilters = new NovaAdvancedFilters();
  });
} else {
  window.NovaFilters = new NovaAdvancedFilters();
}
